// src/admin/shell/AppShell.tsx
// Owns the search-modal state and the global ⌘K shortcut so any element in
// the tree (sidebar, topbar) can open it. Applies theme + density classes
// from useTweaks to scope the design tokens for the whole app surface.
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import SessionTimer from './SessionTimer';
import GlobalSearch from './GlobalSearch';
import TweaksPanel from './TweaksPanel';
import { useTweaks } from './useTweaks';
import styles from './AppShell.module.css';

export default function AppShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const { variant, density } = useTweaks();

  // ⌘K / Ctrl+K toggles the search palette. Lifted from Topbar so the sidebar
  // search button can share the same modal instance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(v => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const themeClass = variant === 'ink' ? 'theme-ink'
    : variant === 'daylight' ? 'theme-daylight' : '';
  const densityClass = `density-${density}`;

  return (
    <div className={`${styles.shell} ${themeClass} ${densityClass}`}>
      <Sidebar onSearchClick={() => setSearchOpen(true)} />
      <div className={styles.main}>
        <Topbar onSearchClick={() => setSearchOpen(true)} />
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
      <SessionTimer />
      <TweaksPanel />
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </div>
  );
}
